import React, { Component } from "react";
import {
  loading,
  NavbarAdmin,
  GET,
  format_date,
  DateRangePicker,
  t // นำเข้าฟังก์ชัน t
} from "../../components/CustomComponent.js";
import { formatHoursMinutes } from "../../utils/formatting";
import { technicianDisplayName } from "../../utils/technician";

export default class WorkOrderSummary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      data_work_orders: [],
      data_priorities: [],
      start_date: this.getDefaultStartDate(),
      end_date: this.getDefaultEndDate(),
    };
  }

  async componentDidMount() {
    loading(true);
    const [woResult, priorityResult] = await Promise.all([
      GET("admin/work-orders", { page: 0 }),
      GET("admin/work-requisition/priorities", { page: 0, page_size: 10000 }),
    ]);

    this.setState({
      data_work_orders: woResult?.status ? woResult.data || [] : [],
      data_priorities: priorityResult?.status ? priorityResult.data || [] : [],
    });
    loading(false);
  }

  getDefaultStartDate = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  };

  getDefaultEndDate = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0);
  };

  toDateOnly = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d.getTime())) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };

  getFilteredOrders = () => {
    const { data_work_orders, start_date, end_date } = this.state;
    const rangeStart = this.toDateOnly(start_date);
    const rangeEnd = this.toDateOnly(end_date || start_date);
    if (!rangeStart || !rangeEnd) return data_work_orders;

    return data_work_orders.filter((item) => {
      const createdDate = this.toDateOnly(item.created_at);
      if (!createdDate) return false;
      return createdDate >= rangeStart && createdDate <= rangeEnd;
    });
  };

  // สีสถานะเดียวกับ Kanban board ของหน้า Work Orders (bg-primary/purple/info/warning/success + danger สำหรับ cancelled)
  getStatusMeta = () => [
    { id: "open", label: t("open"), dotColor: "#0d6efd", badgeBg: "#e0ebff" },
    { id: "assigned", label: t("assigned"), dotColor: "#6f42c1", badgeBg: "#f3e8ff" },
    { id: "start_job", label: t("start_job"), dotColor: "#0dcaf0", badgeBg: "#cff4fc" },
    { id: "check_in", label: t("check_in"), dotColor: "#fd7e14", badgeBg: "#fff0e0" },
    { id: "completed", label: t("completed"), dotColor: "#198754", badgeBg: "#d1e7dd" },
    { id: "cancelled", label: t("cancelled"), dotColor: "#ef4444", badgeBg: "#fee2e2" },
  ];

  getStatusBucketId = (item) => {
    // สถานะ WO ชุดใหม่ (2026-09-10): in_progress -> check_in / en-route -> start_job (รับค่าเก่าด้วยเผื่อยังไม่ migrate)
    const legacyMap = { "en-route": "start_job", en_route: "start_job", in_progress: "check_in", job_done: "close_job" };
    const rawStatus = String(item?.status || "").trim().toLowerCase();
    const status = legacyMap[rawStatus] || rawStatus;

    if (["cancelled", "rejected"].includes(status)) return "cancelled";
    if (["completed", "success", "approved", "closed"].includes(status)) return "completed";
    if (status === "start_job") return "start_job";
    if (["check_in", "check_out", "close_job", "on_site", "working"].includes(status)) return "check_in";

    const hasAssignedTechnician = (item?.visits || []).some(
      (visit) => (visit?.visits_technicians || []).filter(Boolean).length > 0
    );
    if (["assigned", "scheduled"].includes(status) || hasAssignedTechnician) return "assigned";

    return "open";
  };

  getStatusCounts = (filtered) => {
    const meta = this.getStatusMeta();
    return meta.map((m) => ({
      ...m,
      count: filtered.filter((item) => this.getStatusBucketId(item) === m.id).length,
    }));
  };

  buildDonutGradient = (statusCounts, total) => {
    if (!total) return "#e9ecef";
    let cursor = 0;
    const stops = statusCounts.map((s) => {
      const start = cursor;
      cursor += (s.count / total) * 100;
      return `${s.dotColor} ${start}% ${cursor}%`;
    });
    return `conic-gradient(${stops.join(", ")})`;
  };

  getPriorityCounts = (filtered) => {
    return (this.state.data_priorities || []).map((priority) => ({
      uid: priority.uid,
      label: priority.name_th || priority.name_en || "-",
      color: priority.background_color || "#94a3b8",
      count: filtered.filter((item) => item?.priority?.uid === priority.uid).length,
    }));
  };

  getTechnicianCounts = (filtered) => {
    const map = new Map();
    filtered.forEach((item) => {
      (item.visits || []).forEach((visit) => {
        (visit.visits_technicians || []).forEach((vt) => {
          const tech = vt?.technician;
          if (!tech) return;
          const name = technicianDisplayName(tech, "-");
          const key = tech.uid || name;
          const existing = map.get(key);
          // กราฟมีช่องชื่อกว้างจำกัด: label ใช้ชื่อเล่นเหมือนเดิม ส่วน title (hover) เป็นชื่อเต็มมาตรฐาน
          const label = tech.nickname_th || tech.nickname_en || name;
          map.set(key, { name, label, count: (existing?.count || 0) + 1 });
        });
      });
    });
    return [...map.values()].sort((a, b) => b.count - a.count).slice(0, 5);
  };

  getWorkOrderPriorityView = (item) => {
    const priority = item?.priority || null;
    return {
      label: priority?.name_th || priority?.name_en || "-",
      backgroundColor: priority?.background_color || "#E5E7EB",
      color: priority?.font_color || "#374151",
    };
  };

  getWorkOrderTechnicianNames = (item) => {
    const names = (item.visits || [])
      .flatMap((visit) => (visit.visits_technicians || []).map((vt) => vt?.technician))
      .filter(Boolean)
      .map((tech) => technicianDisplayName(tech))
      .filter(Boolean);
    return [...new Set(names)];
  };

  getRecentOrders = (filtered) => {
    const statusMeta = this.getStatusMeta();
    return [...filtered]
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 5)
      .map((item) => {
        const priorityView = this.getWorkOrderPriorityView(item);
        const bucket = this.getStatusBucketId(item);
        const meta = statusMeta.find((m) => m.id === bucket);
        const technicianNames = this.getWorkOrderTechnicianNames(item);

        return {
          uid: item.uid,
          wo_number: item.wo_number || "-",
          title: item.root_cause || item.wo_number || "-",
          priorityLabel: priorityView.label,
          priorityStyle: { backgroundColor: priorityView.backgroundColor, color: priorityView.color },
          statusStyle: { backgroundColor: meta?.badgeBg || "#e9ecef", color: meta?.dotColor || "#374151" },
          statusLabel: meta?.label || String(item.status || "-").replaceAll("_", " ").toUpperCase(),
          technician: technicianNames.length > 0 ? technicianNames.join(", ") : t("unassigned"),
          created: item.created_at ? format_date(item.created_at, "dd/mm/yyyy", "th") : "-",
          completed: bucket === "completed" && item.end_date ? format_date(item.end_date, "dd/mm/yyyy", "th") : "-",
        };
      });
  };

  convertToHours = (value, unit) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return null;
    const u = String(unit || "").toLowerCase();
    if (u.includes("นาที") || u.includes("min")) return num / 60;
    if (u.includes("วัน") || u.includes("day")) return num * 24;
    return num; // ชั่วโมง / hour(s)
  };

  getSummaryStats = (filtered) => {
    const completionDurations = [];
    const responseDurations = [];
    let firstTimeFixEligible = 0;
    let firstTimeFixCount = 0;
    let slaEligible = 0;
    let slaMet = 0;

    filtered.forEach((item) => {
      const createdAt = item.created_at ? new Date(item.created_at) : null;
      const endAt = item.end_date ? new Date(item.end_date) : null;
      const startAt = item.start_date ? new Date(item.start_date) : null;

      if (createdAt && endAt && !isNaN(createdAt) && !isNaN(endAt)) {
        completionDurations.push((endAt - createdAt) / 3600000);
      }

      if (createdAt && startAt && !isNaN(createdAt) && !isNaN(startAt)) {
        const responseHours = (startAt - createdAt) / 3600000;
        responseDurations.push(responseHours);

        const slaHours = this.convertToHours(item?.priority?.sla_response_time_end, item?.priority?.sla_response_time_end_unit);
        if (slaHours !== null) {
          slaEligible += 1;
          if (responseHours <= slaHours) slaMet += 1;
        }
      }

      const bucket = this.getStatusBucketId(item);
      const visitsCount = Array.isArray(item.visits) ? item.visits.length : 0;
      if (bucket === "completed" && visitsCount > 0) {
        firstTimeFixEligible += 1;
        if (visitsCount === 1) firstTimeFixCount += 1;
      }
    });

    const avg = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null);

    return {
      avgCompletionHours: avg(completionDurations),
      avgResponseHours: avg(responseDurations),
      firstTimeFixRate: firstTimeFixEligible ? (firstTimeFixCount / firstTimeFixEligible) * 100 : null,
      slaCompliance: slaEligible ? (slaMet / slaEligible) * 100 : null,
    };
  };

  getAxisTicks = (maxValue, steps = 5) => {
    const safeMax = maxValue > 0 ? maxValue : steps;
    const niceMax = Math.ceil(safeMax / steps) * steps || steps;
    const ticks = [];
    for (let i = steps; i >= 0; i--) ticks.push(Math.round((niceMax / steps) * i));
    return { ticks, niceMax };
  };

  render() {
    const { start_date, end_date } = this.state;
    const filtered = this.getFilteredOrders();
    const total = filtered.length;

    const statusCounts = this.getStatusCounts(filtered);
    const kpiStatusCards = statusCounts.filter((s) => s.id !== "cancelled");
    const donutGradient = this.buildDonutGradient(statusCounts, total);

    const priorityCounts = this.getPriorityCounts(filtered);
    const priorityAxis = this.getAxisTicks(Math.max(0, ...priorityCounts.map((p) => p.count)));

    const technicianCounts = this.getTechnicianCounts(filtered);
    const technicianAxis = this.getAxisTicks(Math.max(0, ...technicianCounts.map((tc) => tc.count)));

    const stats = this.getSummaryStats(filtered);
    const recentOrders = this.getRecentOrders(filtered);

    return (
      <div className="w-100 min-vh-100" style={{ backgroundColor: "#F4F7FE" }}>
        <NavbarAdmin />
        <div className="body p-4 mt-3">

          {/* Header Section */}
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
            <div>
              <h3 className="fw-bold mb-1">{t("work_order_summary_report")}</h3>
              <div className="text-secondary">{t("comprehensive_wo_analysis")}</div>
            </div>
            <div className="d-flex gap-3 align-items-center">
              <div style={{ width: "260px" }}>
                <DateRangePicker
                  type="th"
                  className="form-control bg-white rounded-2 border-0 shadow-sm text-dark"
                  value={{ start_date, end_date }}
                  onChange={(range) =>
                    this.setState({
                      start_date: range.start_date,
                      end_date: range.end_date,
                    })
                  }
                />
              </div>
            </div>
          </div>

          {/* Top KPI Cards */}
          <div className="row g-3 mb-4">
            <div className="col-lg-2 col-md-6">
              <div className="card border-0 rounded-4 shadow-sm p-4 h-100 text-center">
                <div className="text-secondary text-14 fw-bold mb-1">{t("total_wo")}</div>
                <h1 className="fw-bold text-primary mb-0" style={{ fontSize: "3rem" }}>{total}</h1>
              </div>
            </div>
            {kpiStatusCards.map((s) => (
              <div className="col-lg-2 col-md-6" key={s.id}>
                <div className="card border-0 rounded-4 shadow-sm p-4 h-100 text-center" style={{ backgroundColor: s.badgeBg }}>
                  <div className="text-secondary text-14 fw-bold mb-1">{s.label}</div>
                  <h1 className="fw-bold mb-0" style={{ color: s.dotColor, fontSize: "3rem" }}>{s.count}</h1>
                </div>
              </div>
            ))}
          </div>

          {/* Charts Row */}
          <div className="row g-3 mb-4">

            {/* By Status Donut Chart */}
            <div className="col-lg-4">
              <div className="card border-0 rounded-4 shadow-sm p-4 h-100">
                <h6 className="fw-bold text-dark mb-4">{t("by_status")}</h6>
                <div className="d-flex flex-column align-items-center justify-content-center h-100 pb-3">
                  <div
                    className="rounded-circle position-relative mb-4"
                    style={{
                      width: "180px", height: "180px",
                      background: donutGradient
                    }}
                  >
                    <div className="position-absolute top-50 start-50 translate-middle bg-white rounded-circle" style={{ width: "100px", height: "100px" }}></div>
                  </div>

                  <div className="row w-100 px-3 text-12 text-secondary gy-2">
                    {statusCounts.map((s) => (
                      <div className="col-6 d-flex align-items-center" key={s.id}>
                        <div className="wpx-25 hpx-8 rounded me-2" style={{ backgroundColor: s.dotColor }}></div>
                        {s.label} ({s.count})
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* By Priority Bar Chart */}
            <div className="col-lg-4">
              <div className="card border-0 rounded-4 shadow-sm p-4 h-100">
                <h6 className="fw-bold text-dark mb-4">{t("by_priority")}</h6>

                <div className="d-flex flex-column h-100 justify-content-end">
                  <div className="d-flex align-items-end justify-content-around hpx-200 border-bottom border-start pb-0 ps-2 position-relative">
                    <div className="position-absolute top-0 start-0 translate-middle-x ms-1 d-flex flex-column justify-content-between h-100 text-secondary text-10" style={{ width: "25px", textAlign: "right" }}>
                      {priorityAxis.ticks.map((tick, idx) => (
                        <span key={idx}>{tick}</span>
                      ))}
                    </div>
                    {[16.6, 33.3, 50, 66.6, 83.3].map((top) => (
                      <div key={top} className="position-absolute w-100 border-top" style={{ top: `${top}%`, left: "25px", borderColor: "#f1f5f9", zIndex: 0 }}></div>
                    ))}

                    {priorityCounts.length > 0 ? (
                      priorityCounts.map((p) => (
                        <div
                          key={p.uid}
                          className="z-1"
                          style={{ width: "45px", height: `${(p.count / priorityAxis.niceMax) * 100}%`, backgroundColor: p.color }}
                        ></div>
                      ))
                    ) : (
                      <div className="text-secondary text-12 z-1">{t("no_data")}</div>
                    )}
                  </div>
                  <div className="d-flex justify-content-around mt-2 text-secondary text-12">
                    {priorityCounts.map((p) => (
                      <div className="text-center text-truncate" style={{ width: "45px" }} key={p.uid} title={p.label}>{p.label}</div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* By Technician Horizontal Bar Chart */}
            <div className="col-lg-4">
              <div className="card border-0 rounded-4 shadow-sm p-4 h-100">
                <h6 className="fw-bold text-dark mb-4">{t("by_technician")}</h6>

                <div className="d-flex flex-column h-100 justify-content-end">
                  <div className="position-relative h-100 d-flex flex-column justify-content-between border-start border-bottom pb-2">

                    {[25, 50, 75, 100].map((left) => (
                      <div key={left} className="position-absolute h-100 border-start" style={{ left: `${left}%`, borderColor: "#f1f5f9", zIndex: 0 }}></div>
                    ))}

                    {technicianCounts.length > 0 ? (
                      technicianCounts.map((tc, idx) => (
                        <div className={`d-flex align-items-center z-1 mt-2 ${idx === technicianCounts.length - 1 ? "mb-2" : "mb-3"}`} key={idx}>
                          <div className="text-secondary text-12 text-end pe-2 flex-shrink-0 text-truncate" style={{ width: "60px" }} title={tc.name}>{tc.label}</div>
                          <div className="bg-primary hpx-25" style={{ width: `${(tc.count / technicianAxis.niceMax) * 100}%` }}></div>
                        </div>
                      ))
                    ) : (
                      <div className="text-secondary text-12 z-1 mt-2">{t("no_data")}</div>
                    )}
                  </div>

                  <div className="d-flex justify-content-between ms-5 ps-2 mt-2 text-secondary text-10">
                    {technicianAxis.ticks
                      .slice()
                      .reverse()
                      .map((tick, idx) => (
                        <span key={idx}>{tick}</span>
                      ))}
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* 4 Colored Summary Blocks */}
          <div className="row g-3 mb-4">
            <div className="col-lg-3 col-md-6">
              <div className="card border-0 rounded-3 shadow-sm p-4 text-white" style={{ backgroundColor: "#3b82f6" }}>
                <div className="text-white text-14 fw-bold mb-1">{t("avg_completion_time")}</div>
                <h2 className="fw-bold mb-0">{stats.avgCompletionHours !== null ? `${formatHoursMinutes(stats.avgCompletionHours)} ${t("hrs_minutes")}` : "-"}</h2>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="card border-0 rounded-3 shadow-sm p-4 text-white" style={{ backgroundColor: "#22c55e" }}>
                <div className="text-white text-14 fw-bold mb-1">{t("first_time_fix_rate")}</div>
                <h2 className="fw-bold mb-0">{stats.firstTimeFixRate !== null ? `${stats.firstTimeFixRate.toFixed(1)}%` : "-"}</h2>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="card border-0 rounded-3 shadow-sm p-4 text-white" style={{ backgroundColor: "#a855f7" }}>
                <div className="text-white text-14 fw-bold mb-1">{t("avg_response_time")}</div>
                <h2 className="fw-bold mb-0">{stats.avgResponseHours !== null ? `${formatHoursMinutes(stats.avgResponseHours)} ${t("hrs_minutes")}` : "-"}</h2>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="card border-0 rounded-3 shadow-sm p-4 text-white" style={{ backgroundColor: "#f97316" }}>
                <div className="text-white text-14 fw-bold mb-1">{t("sla_compliance")}</div>
                <h2 className="fw-bold mb-0">{stats.slaCompliance !== null ? `${stats.slaCompliance.toFixed(1)}%` : "-"}</h2>
              </div>
            </div>
          </div>

          {/* Recent Work Orders Table Card */}
          <div className="card border-0 rounded-4 shadow-sm p-0 mb-4 overflow-hidden">
            <div className="p-4 border-bottom">
              <h6 className="fw-bold text-dark mb-0">{t("recent_work_orders")}</h6>
            </div>

            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="bg-light">
                  <tr className="text-dark fw-bold text-14">
                    <th className="py-3 px-4 border-0">{t("wo_number")}</th>
                    <th className="py-3 border-0">{t("title")}</th>
                    <th className="py-3 border-0">{t("priority")}</th>
                    <th className="py-3 border-0">{t("status")}</th>
                    <th className="py-3 border-0">{t("technician")}</th>
                    <th className="py-3 border-0">{t("created")}</th>
                    <th className="py-3 border-0">{t("completed")}</th>
                  </tr>
                </thead>
                <tbody>
                  {recentOrders.length > 0 ? (
                    recentOrders.map((item) => (
                      <tr key={item.uid} className="border-bottom">
                        <td className="py-3 px-4 fw-bold text-dark">{item.wo_number}</td>
                        <td className="text-dark text-14">{item.title}</td>
                        <td>
                          <span className="badge rounded-pill fw-bold text-10 px-3 py-1" style={item.priorityStyle}>
                            {item.priorityLabel}
                          </span>
                        </td>
                        <td>
                          <span className="badge rounded-pill fw-bold text-12 px-3 py-1" style={item.statusStyle}>
                            {item.statusLabel}
                          </span>
                        </td>
                        <td className="text-dark text-14">{item.technician}</td>
                        <td className="text-dark text-14">{item.created}</td>
                        <td className="text-dark text-14">{item.completed}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center text-secondary py-4">{t("no_data")}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>
    );
  }
}
